/**
 * ChatBox component: Main chat interface
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, AlertCircle, X, Mic, MicOff, Bug, Image as ImageIcon } from 'lucide-react';
import { useChat } from './useChat';
import ActionLogModal from './ActionLogModal';
import './ChatBox.css';
import { API_BASE } from '../config/api';

interface ChatBoxProps {
  userId: string;
  context?: {
    currentRoute?: string;
    selectedFounders?: string[];
    activeTab?: { id: string; name: string } | null;
  };
  onClose?: () => void;
  onRefreshFounders?: () => void;
}

const ChatBox: React.FC<ChatBoxProps> = ({ userId, context, onClose, onRefreshFounders }) => {
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const {
    messages,
    sendMessage,
    isLoading,
    // sessionId, // Commented out: unused variable
    endConversation,
    startNewSession,
    actionLogData,
    showActionLog,
    setShowActionLog,
    mode,
    setMode,
    bugReportType,
    setBugReportType,
    screenshot,
    setScreenshot,
  } = useChat(userId, { ...context, onRefreshFounders });
  
  // Check if this is phone/text mode (no screenshot support)
  const isPhoneMode = context?.currentRoute?.includes('text') || false;

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const currentScreenshot = screenshot;
    const currentBugType = bugReportType;

    // Clear input and screenshot immediately for better UX
    setInput('');
    setScreenshot(null);

    await sendMessage(input, currentScreenshot, currentBugType);
    inputRef.current?.focus();
  };
  
  // Handle screenshot paste
  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Skip screenshot handling in phone mode
    if (isPhoneMode) return;
    
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            setScreenshot(base64String);
          };
          reader.readAsDataURL(blob);
        }
        break;
      }
    }
  };
  
  const handleRemoveScreenshot = () => {
    setScreenshot(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        handleRecordingComplete();
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleRecordingComplete = async () => {
    if (audioChunksRef.current.length === 0) return;

    setIsTranscribing(true);
    
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/whisper/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      
      if (data.success && data.transcription) {
        setInput(data.transcription);
        inputRef.current?.focus();
      } else {
        console.error('Transcription failed:', data.error);
        alert('Failed to transcribe audio. Please try again.');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      alert('Error transcribing audio. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <>
      <ActionLogModal
        isOpen={showActionLog}
        onClose={() => setShowActionLog(false)}
        data={actionLogData || {}}
      />
      
      <div className="chatbox-container">
        <div className="chatbox-header">
          <h3>REMI 🐭</h3>

          <div className="chatbox-header-actions">
          {context?.selectedFounders && context.selectedFounders.length > 0 && (
            <span className="context-chip">
              {context.selectedFounders.length} selected
            </span>
          )}
          
          {messages.length > 0 && (
            <button 
              className="chatbox-new-button" 
              disabled={isLoading}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🆕 New conversation button clicked');
                startNewSession();
              }}
              aria-label="Start new conversation"
              title="Start new conversation"
            >
              New
            </button>
          )}
          
          {onClose && (
            <button 
              className="chatbox-close-button" 
              disabled={isLoading}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Close button clicked');
                
                // Close immediately for better UX
                onClose();
                
                // Process actions in background (don't await)
                endConversation().catch(error => {
                  console.error('❌ Error processing conversation:', error);
                });
              }}
              onMouseDown={(e) => {
                console.log('🖱️ Close button mouse down');
              }}
              onMouseUp={(e) => {
                console.log('🖱️ Close button mouse up');
              }}
              aria-label="Close chat"
              title="Close chat"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="chatbox-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message message-${msg.role}`}>
            <div className="message-content">
              {msg.screenshot && (
                <div className="message-screenshot">
                  <img src={msg.screenshot} alt="Attached screenshot" />
                </div>
              )}
              {msg.content}
              {msg.error && (
                <div className="message-error">
                  <AlertCircle size={14} />
                  <span>{msg.error}</span>
                </div>
              )}
            </div>
            <div className="message-time">
              {msg.timestamp.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message message-assistant">
            <div className="message-content">
              <div className="typing-indicator">
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
              </div>
            </div>
          </div>
        )}

        {isRecording && (
          <div className="message message-user">
            <div className="message-content">
              <div className="listening-indicator">
                <div className="listening-text">Listening...</div>
                <div className="listening-animation">
                  <div className="listening-bar"></div>
                  <div className="listening-bar"></div>
                  <div className="listening-bar"></div>
                  <div className="listening-bar"></div>
                  <div className="listening-bar"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {isTranscribing && (
          <div className="message message-assistant">
            <div className="message-content">
              <div className="transcribing-indicator">
                <div className="transcribing-text">🔄 Transcribing...</div>
                <div className="transcribing-spinner"></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form className="chatbox-input-form" onSubmit={handleSubmit}>
        {/* Screenshot preview */}
        {screenshot && !isPhoneMode && (
          <div className="chatbox-screenshot-preview">
            <img src={screenshot} alt="Screenshot preview" />
            <button
              type="button"
              onClick={handleRemoveScreenshot}
              className="chatbox-screenshot-remove"
              title="Remove screenshot"
            >
              <X size={14} />
            </button>
          </div>
        )}
        <div className="chatbox-input-row">
          <div className="chatbox-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              className="chatbox-input"
              placeholder={
                bugReportType === 'bug' 
                  ? "Describe the bug..." 
                  : "Ask me anything..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              disabled={isLoading}
            />
            {screenshot && !isPhoneMode && (
              <div className="chatbox-screenshot-indicator" title="Screenshot attached">
                <ImageIcon size={16} />
              </div>
            )}
          </div>
          <button
            type="button"
            className={`chatbox-mic-button ${isRecording ? 'recording' : ''}`}
            title={isRecording ? 'Stop recording' : 'Start voice input'}
            onClick={handleMicClick}
            disabled={isLoading || isTranscribing}
          >
            {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button
            type="submit"
            className="chatbox-send-button"
            disabled={!input.trim() || isLoading}
          >
            <Send size={18} />
          </button>
        </div>
      </form>
      
      {/* Mode Toggle */}
      <div className="mode-toggle-bottom">
        <button
          className={`mode-button ${mode === 'agent' ? 'active' : ''}`}
          onClick={() => setMode('agent')}
          title="Agent mode - Can create, update, delete"
        >
          Agent
        </button>
        <button
          className={`mode-button ${mode === 'ask' ? 'active' : ''}`}
          onClick={() => setMode('ask')}
          title="Ask mode - Information only"
        >
          Ask
        </button>
        <button
          className={`mode-button ${bugReportType ? 'active' : ''}`}
          onClick={() => {
            // Toggle between null and 'bug'
            if (bugReportType === null) {
              setBugReportType('bug');
            } else {
              setBugReportType(null);
              setScreenshot(null); // Clear screenshot when toggling off
            }
          }}
          title={bugReportType === 'bug' ? 'Bug Report Mode' : 'Bug Report'}
        >
          <span>🐞</span>
          <span>Bug</span>
        </button>
      </div>
      </div>
    </>
  );
};

export default ChatBox;

